import { type FC, Suspense, useState } from "react";
import { Spinner, Table } from "react-bootstrap";
import { ErrorBoundary } from "react-error-boundary";
import { Link, useNavigate } from "react-router";
import {
	TIKTOK_ADS_REPORT_RANGES,
	type TikTokAdsAdGroupPerformance,
	type TikTokAdsAdPerformance,
	type TikTokAdsCampaignPerformance,
	type TikTokAdsMetrics,
	type TikTokAdsReallocationProposal,
} from "shared";
import { useModal } from "../common/modal/Modal";
import { Button } from "../components/Button";
import { Card } from "../components/Card";
import { Select } from "../components/Select";
import { LinkGen } from "../LinkGen";
import { AppPage } from "../layout/AppLayout";
import { useTikTokAds } from "../settings/useIntegrations";
import { ModalTikTokReallocationConfirm } from "./ModalTikTokReallocationConfirm";
import {
	TikTokBudgetEditor,
	TikTokStatusToggle,
} from "./TikTokCampaignActions";
import { TikTokInsightsTrendChart } from "./TikTokInsightsTrendChart";
import { useTikTokAdsReport, useTikTokAdsSync } from "./useTikTokAdsReporting";
import {
	useTikTokAdsCampaignActions,
	useTikTokAdsDraftActions,
	useTikTokAdsDrafts,
} from "./useTikTokCampaigns";

const formatCurrency = (value: number, currency?: string) => {
	const amount = value.toLocaleString(undefined, {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
	return currency ? `${amount} ${currency}` : `$${amount}`;
};

const formatNumber = (value: number) =>
	value.toLocaleString(undefined, { maximumFractionDigits: 0 });

const formatPercent = (value: number) => `${value.toFixed(2)}%`;

const KPI_CARDS: {
	key: keyof TikTokAdsMetrics;
	label: string;
	kind: "currency" | "number" | "percent";
}[] = [
	{ key: "spend", label: "Spend", kind: "currency" },
	{ key: "impressions", label: "Impressions", kind: "number" },
	{ key: "reach", label: "Reach", kind: "number" },
	{ key: "clicks", label: "Clicks", kind: "number" },
	{ key: "ctr", label: "CTR", kind: "percent" },
	{ key: "cpc", label: "CPC", kind: "currency" },
	{ key: "cpm", label: "CPM", kind: "currency" },
	{ key: "videoViews", label: "Video views", kind: "number" },
	{ key: "conversions", label: "Conversions", kind: "number" },
	{ key: "costPerConversion", label: "Cost/conv.", kind: "currency" },
];

const CAMPAIGN_PAGE_SIZE = 10;
const DRAFT_PAGE_SIZE = 5;

const MetricValue: FC<{
	value: number;
	kind: "currency" | "number" | "percent";
	currency?: string;
}> = ({ value, kind, currency }) => {
	if (kind === "currency") {
		return <>{formatCurrency(value, currency)}</>;
	}
	return <>{kind === "percent" ? formatPercent(value) : formatNumber(value)}</>;
};

const StatusBadge: FC<{ status: string }> = ({ status }) => (
	<span className={`badge bg-${status === "ENABLE" ? "success" : "secondary"}`}>
		{status === "ENABLE" ? "Active" : "Paused"}
	</span>
);

const AdRow: FC<{
	ad: TikTokAdsAdPerformance;
	currency?: string;
}> = ({ ad, currency }) => (
	<tr style={{ background: "var(--bs-secondary-bg, #e9ecef)" }}>
		<td style={{ paddingLeft: 56 }}>
			<div style={{ fontWeight: 500, fontSize: "0.85rem" }}>
				└── Video ad: {ad.name}
			</div>
			<div style={{ fontSize: "0.7rem", color: "var(--bs-secondary)" }}>
				Ad · {ad.adId}
			</div>
		</td>
		<td>
			<StatusBadge status={ad.status} />
		</td>
		<td className="text-end">{formatCurrency(ad.spend, currency)}</td>
		<td className="text-end">{formatNumber(ad.impressions)}</td>
		<td className="text-end">{formatNumber(ad.clicks)}</td>
		<td className="text-end">{formatPercent(ad.ctr)}</td>
		<td className="text-end">{formatCurrency(ad.cpc, currency)}</td>
		<td className="text-end">{formatNumber(ad.conversions)}</td>
		<td className="text-end">—</td>
		<td className="text-end">—</td>
		<td />
	</tr>
);

const AdGroupRow: FC<{
	group: TikTokAdsAdGroupPerformance;
	currency?: string;
	days: number;
}> = ({ group, currency, days }) => {
	const { updateAdGroupStatus, updateAdGroupBudget } =
		useTikTokAdsCampaignActions(days);
	const ads = group.ads ?? [];
	const [expanded, setExpanded] = useState(ads.length > 0);

	return (
		<>
			<tr style={{ background: "var(--bs-tertiary-bg, #f8f9fa)" }}>
				<td style={{ paddingLeft: 28 }}>
					<div style={{ display: "flex", gap: 8, alignItems: "center" }}>
						{ads.length > 0 && (
							<Button
								size="sm"
								variant="secondary"
								onClick={() => setExpanded((v) => !v)}
								style={{ padding: "0 6px", minWidth: 28 }}
							>
								{expanded ? "−" : "+"}
							</Button>
						)}
						<div>
							<div style={{ fontWeight: 500, fontSize: "0.9rem" }}>
								└── Ad group: {group.name}
							</div>
							<div style={{ fontSize: "0.7rem", color: "var(--bs-secondary)" }}>
								Ad group · {group.adGroupId}
								{ads.length > 0
									? ` · ${ads.length} ad${ads.length === 1 ? "" : "s"}`
									: ""}
							</div>
						</div>
					</div>
				</td>
				<td>
					<StatusBadge status={group.status} />
				</td>
				<td className="text-end">{formatCurrency(group.spend, currency)}</td>
				<td className="text-end">{formatNumber(group.impressions)}</td>
				<td className="text-end">{formatNumber(group.clicks)}</td>
				<td className="text-end">{formatPercent(group.ctr)}</td>
				<td className="text-end">{formatCurrency(group.cpc, currency)}</td>
				<td className="text-end">{formatNumber(group.conversions)}</td>
				<td className="text-end">
					{group.conversions
						? formatCurrency(group.costPerConversion, currency)
						: "—"}
				</td>
				<td className="text-end">
					<TikTokBudgetEditor
						objectId={group.adGroupId}
						currentBudget={group.budget}
						isPending={updateAdGroupBudget.isPending}
						promptNoun="ad group"
						onSave={async (adGroupId, budget) => {
							await updateAdGroupBudget.mutateAsync({ adGroupId, budget });
						}}
					/>
				</td>
				<td>
					<TikTokStatusToggle
						objectId={group.adGroupId}
						status={group.status}
						label="ad group"
						isPending={updateAdGroupStatus.isPending}
						onToggle={(adGroupId, status) =>
							updateAdGroupStatus.mutate({ adGroupId, status })
						}
					/>
				</td>
			</tr>
			{expanded &&
				ads.map((ad) => <AdRow key={ad.adId} ad={ad} currency={currency} />)}
		</>
	);
};

const CampaignRow: FC<{
	campaign: TikTokAdsCampaignPerformance;
	currency?: string;
	days: number;
}> = ({ campaign, currency, days }) => {
	const { updateStatus, updateBudget } = useTikTokAdsCampaignActions(days);
	const adGroups = campaign.adGroups ?? [];
	const [expanded, setExpanded] = useState(adGroups.length > 0);

	return (
		<>
			<tr>
				<td>
					<div style={{ display: "flex", gap: 8, alignItems: "center" }}>
						{adGroups.length > 0 && (
							<Button
								size="sm"
								variant="secondary"
								onClick={() => setExpanded((v) => !v)}
								style={{ padding: "0 6px", minWidth: 28 }}
							>
								{expanded ? "−" : "+"}
							</Button>
						)}
						<div>
							<div style={{ fontWeight: 600 }}>Campaign: {campaign.name}</div>
							<div
								style={{ fontSize: "0.75rem", color: "var(--bs-secondary)" }}
							>
								{campaign.objective ? `${campaign.objective} · ` : ""}
								{campaign.campaignId}
								{adGroups.length > 0
									? ` · ${adGroups.length} ad group${adGroups.length === 1 ? "" : "s"}`
									: ""}
							</div>
						</div>
					</div>
				</td>
				<td>
					<StatusBadge status={campaign.status} />
				</td>
				<td className="text-end">{formatCurrency(campaign.spend, currency)}</td>
				<td className="text-end">{formatNumber(campaign.impressions)}</td>
				<td className="text-end">{formatNumber(campaign.clicks)}</td>
				<td className="text-end">{formatPercent(campaign.ctr)}</td>
				<td className="text-end">{formatCurrency(campaign.cpc, currency)}</td>
				<td className="text-end">{formatNumber(campaign.conversions)}</td>
				<td className="text-end">
					{campaign.conversions
						? formatCurrency(campaign.costPerConversion, currency)
						: "—"}
				</td>
				<td className="text-end">
					<TikTokBudgetEditor
						objectId={campaign.campaignId}
						currentBudget={campaign.budget}
						isPending={updateBudget.isPending}
						onSave={async (campaignId, budget) => {
							await updateBudget.mutateAsync({ campaignId, budget });
						}}
					/>
				</td>
				<td>
					<TikTokStatusToggle
						objectId={campaign.campaignId}
						status={campaign.status}
						label="campaign"
						isPending={updateStatus.isPending}
						onToggle={(campaignId, status) =>
							updateStatus.mutate({ campaignId, status })
						}
					/>
				</td>
			</tr>
			{expanded &&
				adGroups.map((group) => (
					<AdGroupRow
						key={group.adGroupId}
						group={group}
						currency={currency}
						days={days}
					/>
				))}
		</>
	);
};

const ReallocationCard: FC<{
	proposal?: TikTokAdsReallocationProposal;
	days: number;
}> = ({ proposal, days }) => {
	const { open } = useModal();
	const { applyReallocation } = useTikTokAdsCampaignActions(days);

	if (!proposal) {
		return null;
	}

	const hasActions = proposal.actions.length > 0;

	const openReview = () => {
		open(ModalTikTokReallocationConfirm, {
			summary: proposal.summary,
			actions: proposal.actions,
			onConfirm: async (selected) => {
				await applyReallocation.mutateAsync(selected);
			},
		});
	};

	return (
		<Card style={{ marginTop: 16 }}>
			<Card.Header>Moxie recommendation</Card.Header>
			<Card.Body>
				<p className="text-secondary mb-2">{proposal.summary}</p>
				{hasActions ? (
					<>
						<ul style={{ marginBottom: 12 }}>
							{proposal.actions.map((action) => (
								<li key={`${action.type}-${action.adGroupId}`}>
									{action.type === "PAUSE_ADGROUP" ? (
										<>
											Pause <strong>{action.adGroupName}</strong>
										</>
									) : (
										<>
											Move budget on <strong>{action.adGroupName}</strong> to $
											{action.proposedBudget}/day
										</>
									)}
									<div
										className="text-secondary"
										style={{ fontSize: "0.85rem" }}
									>
										{action.reason}
									</div>
								</li>
							))}
						</ul>
						<Button onClick={openReview} disabled={applyReallocation.isPending}>
							Review changes
						</Button>
					</>
				) : null}
			</Card.Body>
		</Card>
	);
};

const DraftsCard: FC = () => {
	const navigate = useNavigate();
	const { data: drafts } = useTikTokAdsDrafts();
	const { deleteDraft, publishDraft } = useTikTokAdsDraftActions();
	const [page, setPage] = useState(0);

	const unpublished = drafts.filter((draft) => !draft.tiktokAdId);
	if (unpublished.length === 0) {
		return null;
	}

	const totalPages = Math.max(
		1,
		Math.ceil(unpublished.length / DRAFT_PAGE_SIZE),
	);
	const safePage = Math.min(page, totalPages - 1);
	const pageDrafts = unpublished.slice(
		safePage * DRAFT_PAGE_SIZE,
		(safePage + 1) * DRAFT_PAGE_SIZE,
	);

	return (
		<Card style={{ marginTop: 16 }}>
			<Card.Header>Unpublished drafts</Card.Header>
			<Card.Body>
				<Table responsive hover className="mb-0">
					<thead>
						<tr>
							<th>Name</th>
							<th>Objective</th>
							<th className="text-end">Budget</th>
							<th>Status</th>
							<th>Last edited</th>
							<th />
						</tr>
					</thead>
					<tbody>
						{pageDrafts.map((draft) => (
							<tr key={draft.id}>
								<td>
									<button
										type="button"
										className="btn btn-link p-0 text-start"
										onClick={() =>
											navigate(LinkGen.TikTokAds.DraftEdit(draft.id))
										}
									>
										{draft.name || "(unnamed)"}
									</button>
								</td>
								<td>{draft.objective}</td>
								<td className="text-end">{draft.budgetAmount}</td>
								<td>
									{draft.tiktokCampaignId ? (
										<span className="badge bg-warning text-dark">
											Publish failed
										</span>
									) : (
										<span className="badge bg-secondary">Draft</span>
									)}
								</td>
								<td>{new Date(draft.updatedAt).toLocaleDateString()}</td>
								<td className="text-end">
									<div
										style={{
											display: "flex",
											gap: 8,
											justifyContent: "flex-end",
										}}
									>
										<Button
											size="sm"
											variant="secondary"
											onClick={() =>
												navigate(LinkGen.TikTokAds.DraftEdit(draft.id))
											}
										>
											Edit
										</Button>
										{draft.tiktokCampaignId ? (
											<Button
												size="sm"
												disabled={publishDraft.isPending}
												onClick={() => publishDraft.mutate(draft.id)}
											>
												Retry
											</Button>
										) : null}
										<Button
											size="sm"
											variant="secondary"
											disabled={deleteDraft.isPending}
											onClick={() => deleteDraft.mutate(draft.id)}
										>
											Delete
										</Button>
									</div>
								</td>
							</tr>
						))}
					</tbody>
				</Table>
				{totalPages > 1 && (
					<div
						style={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							gap: 8,
							paddingTop: 12,
						}}
					>
						<Button
							size="sm"
							variant="secondary"
							disabled={safePage === 0}
							onClick={() => setPage((p) => Math.max(0, p - 1))}
						>
							Previous
						</Button>
						<span style={{ fontSize: "0.9rem" }}>
							Page {safePage + 1} of {totalPages}
							<span className="text-secondary">
								{" "}
								· {unpublished.length} drafts
							</span>
						</span>
						<Button
							size="sm"
							variant="secondary"
							disabled={safePage >= totalPages - 1}
							onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
						>
							Next
						</Button>
					</div>
				)}
			</Card.Body>
		</Card>
	);
};

const ReportContent: FC<{ days: number }> = ({ days }) => {
	const { data: report } = useTikTokAdsReport(days);
	const currency = report.currency;
	const [campaignPage, setCampaignPage] = useState(0);

	const totalCampaignPages = Math.max(
		1,
		Math.ceil(report.campaigns.length / CAMPAIGN_PAGE_SIZE),
	);
	const safeCampaignPage = Math.min(campaignPage, totalCampaignPages - 1);
	const pageCampaigns = report.campaigns.slice(
		safeCampaignPage * CAMPAIGN_PAGE_SIZE,
		(safeCampaignPage + 1) * CAMPAIGN_PAGE_SIZE,
	);

	return (
		<>
			<div
				style={{
					display: "grid",
					gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
					gap: 12,
				}}
			>
				{KPI_CARDS.map((card) => (
					<Card key={card.key}>
						<Card.Body style={{ textAlign: "center", padding: "12px 8px" }}>
							<div
								style={{
									fontSize: "0.75rem",
									color: "var(--bs-secondary)",
									textTransform: "uppercase",
									marginBottom: 4,
								}}
							>
								{card.label}
							</div>
							<div style={{ fontSize: "1.25rem", fontWeight: 600 }}>
								<MetricValue
									value={report.totals[card.key]}
									kind={card.kind}
									currency={currency}
								/>
							</div>
						</Card.Body>
					</Card>
				))}
			</div>

			<div style={{ marginTop: 16 }}>
				<TikTokInsightsTrendChart data={report.daily} />
			</div>

			<Card style={{ marginTop: 16 }}>
				<Card.Header>Campaign → Ad group → Ad</Card.Header>
				<Card.Body>
					{report.campaigns.length === 0 ? (
						<p className="text-secondary mb-0">
							No campaigns in the last refresh. Use{" "}
							<strong>Refresh data</strong> to pull the latest from TikTok.
						</p>
					) : (
						<>
							<Table responsive hover className="mb-0">
								<thead>
									<tr>
										<th>Structure</th>
										<th>Status</th>
										<th className="text-end">Spend</th>
										<th className="text-end">Impressions</th>
										<th className="text-end">Clicks</th>
										<th className="text-end">CTR</th>
										<th className="text-end">CPC</th>
										<th className="text-end">Conv.</th>
										<th className="text-end">Cost/conv.</th>
										<th className="text-end">Budget</th>
										<th />
									</tr>
								</thead>
								<tbody>
									{pageCampaigns.map((campaign) => (
										<CampaignRow
											key={campaign.campaignId}
											campaign={campaign}
											currency={currency}
											days={days}
										/>
									))}
								</tbody>
							</Table>
							{totalCampaignPages > 1 && (
								<div
									style={{
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
										gap: 8,
										paddingTop: 12,
									}}
								>
									<Button
										size="sm"
										variant="secondary"
										disabled={safeCampaignPage === 0}
										onClick={() => setCampaignPage((p) => Math.max(0, p - 1))}
									>
										Previous
									</Button>
									<span style={{ fontSize: "0.9rem" }}>
										Page {safeCampaignPage + 1} of {totalCampaignPages}
										<span className="text-secondary">
											{" "}
											· {report.campaigns.length} campaigns
										</span>
									</span>
									<Button
										size="sm"
										variant="secondary"
										disabled={safeCampaignPage >= totalCampaignPages - 1}
										onClick={() =>
											setCampaignPage((p) =>
												Math.min(totalCampaignPages - 1, p + 1),
											)
										}
									>
										Next
									</Button>
								</div>
							)}
						</>
					)}
				</Card.Body>
				{report.lastSyncedAt && (
					<Card.Footer
						style={{ fontSize: "0.8rem" }}
						className="text-secondary"
					>
						Last refreshed {new Date(report.lastSyncedAt).toLocaleString()}
					</Card.Footer>
				)}
			</Card>

			<ReallocationCard proposal={report.reallocation} days={days} />

			<ErrorBoundary fallback={null}>
				<Suspense fallback={null}>
					<DraftsCard />
				</Suspense>
			</ErrorBoundary>
		</>
	);
};

const NotConnected: FC<{ needsAdvertiser: boolean }> = ({
	needsAdvertiser,
}) => (
	<Card>
		<Card.Body>
			<h5>
				{needsAdvertiser
					? "Choose an advertiser account"
					: "TikTok Ads is not connected"}
			</h5>
			<p className="text-secondary">
				{needsAdvertiser
					? "Your TikTok Ads connection is active but no advertiser account is selected yet."
					: "Connect your TikTok Ads account to see campaign performance here."}
			</p>
			<Link to={LinkGen.Settings.Apps()} className="btn btn-primary">
				Go to Settings → Apps
			</Link>
		</Card.Body>
	</Card>
);

export const PageTikTokAds: FC = () => {
	const [days, setDays] = useState<number>(30);
	const { status } = useTikTokAds();
	const sync = useTikTokAdsSync(days);
	const navigate = useNavigate();

	const connected = status.data.connected;
	const advertiser = status.data.advertiser;
	const ready = connected && !!advertiser;

	return (
		<AppPage
			title="TikTok Ads"
			subtitle={
				advertiser
					? `${status.data.sandboxMode ? "Sandbox · " : ""}${advertiser.name} · last ${days} days`
					: "Campaign performance"
			}
			headerActions={
				ready && (
					<div style={{ display: "flex", gap: 8, alignItems: "center" }}>
						<Select
							size="sm"
							value={days}
							onChange={(e) => setDays(Number(e.target.value))}
							style={{ width: "auto" }}
						>
							{TIKTOK_ADS_REPORT_RANGES.map((range) => (
								<option key={range} value={range}>
									Last {range} days
								</option>
							))}
						</Select>
						<Button
							size="sm"
							variant="secondary"
							onClick={() => sync.mutate()}
							disabled={sync.isPending}
						>
							{sync.isPending ? "Refreshing…" : "Refresh data"}
						</Button>
						<Button
							size="sm"
							onClick={() => navigate(LinkGen.TikTokAds.CampaignCreate())}
						>
							Create campaign
						</Button>
					</div>
				)
			}
		>
			{ready ? (
				<ErrorBoundary
					fallback={
						<Card>
							<Card.Body>
								Could not load TikTok Ads performance data. Try refreshing.
							</Card.Body>
						</Card>
					}
				>
					<Suspense fallback={<Spinner />}>
						<ReportContent days={days} />
					</Suspense>
				</ErrorBoundary>
			) : (
				<NotConnected needsAdvertiser={connected} />
			)}
		</AppPage>
	);
};
